'use client';
import { CaretLeftIcon, CaretRightIcon, XIcon, EyeIcon } from '@phosphor-icons/react';
import Solutions from '../../Solutions/Solutions';
import { Button } from '../ui/button';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export default function SolutionDrawer({
  qNo,
  questionNumbers,
  handleClose,
  isOpen,
  packId,
  testId,
  showReportButton = true,
  isPackAnalysis = false,
  questionDataArray = [],
  onQuestionChange = null,
  currentQnoIndex = 0,
  setCurrentQnoIndex = () => {},
}) {
  const [questionNumber, setQuestionNumber] = useState(qNo);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isRendered, setIsRendered] = useState(false);
  const [portalRoot, setPortalRoot] = useState(null);
  const [validPackId, setValidPackId] = useState(null);
  const [currentQuestionData, setCurrentQuestionData] = useState(null);
  const [showSolution, setShowSolution] = useState(false); // New state to control solution visibility

  useEffect(() => {
    // Validate packId format (24 character hex string)
    if (packId && /^[0-9a-fA-F]{24}$/.test(packId)) {
      setValidPackId(packId);
    } else {
      setValidPackId(null);
    }

    if (typeof window !== 'undefined') {
      let portalContainer = document.getElementById('drawer-portal-root');
      if (!portalContainer) {
        portalContainer = document.createElement('div');
        portalContainer.id = 'drawer-portal-root';
        portalContainer.style.position = 'relative';
        portalContainer.style.zIndex = '2147483647';
        document.body.appendChild(portalContainer);
      }
      setPortalRoot(portalContainer);
    }

    return () => {
      const portalContainer = document.getElementById('drawer-portal-root');
      if (portalContainer && !portalContainer.hasChildNodes()) {
        document.body.removeChild(portalContainer);
      }
    };
  }, [packId]);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
      document.body.style.overflow = 'hidden';
      // Reset showSolution when drawer opens
      setShowSolution(false);

      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsAnimating(false);
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  useEffect(() => {
    if (isPackAnalysis && questionDataArray.length > 0) {
      setQuestionNumber(qNo);
      const currentIndex = questionNumbers.findIndex((qNum) => qNum === qNo);
      if (currentIndex >= 0 && currentIndex < questionDataArray.length) {
        setCurrentQuestionData(questionDataArray[currentIndex]);
      }
    }
  }, [qNo, isPackAnalysis, questionDataArray, questionNumbers]);

  useEffect(() => {
    if (isPackAnalysis) {
      setShowSolution(false);
    } else {
      setShowSolution(false);
    }
  }, [qNo, isPackAnalysis ? questionDataArray : null]);

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsRendered(false);
      document.body.style.overflow = 'unset';
    }
  };

  const handleQuestionChange = (isPrev) => {
    const currentQNoIndex = isPackAnalysis ? currentQnoIndex : questionNumbers.indexOf(questionNumber);

    let newQuestionNumber;
    let newIndex;
    if (isPackAnalysis) {
      if (isPrev) {
        if (currentQNoIndex !== 0) {
          newIndex = currentQNoIndex - 1;
          newQuestionNumber = questionNumbers[newIndex];
          setCurrentQnoIndex(newIndex);
        } else {
          return;
        }
      } else {
        if (currentQNoIndex < questionNumbers.length - 1) {
          newIndex = currentQNoIndex + 1;
          newQuestionNumber = questionNumbers[newIndex];
          console.log(newIndex);
          setCurrentQnoIndex(newIndex);
        } else {
          return;
        }
      }
    } else {
      if (isPrev) {
        if (questionNumber !== questionNumbers[0]) {
          newIndex = currentQNoIndex - 1;
          newQuestionNumber = questionNumbers[newIndex];
        } else {
          return; // Already at first question
        }
      } else {
        if (questionNumber !== questionNumbers[questionNumbers.length - 1]) {
          newIndex = currentQNoIndex + 1;
          newQuestionNumber = questionNumbers[newIndex];
        } else {
          return; // Already at last question
        }
      }
    }

    setQuestionNumber(newQuestionNumber);

    // Update current question data for pack analysis
    if (isPackAnalysis && questionDataArray.length > 0 && newIndex >= 0 && newIndex < questionDataArray.length) {
      setCurrentQuestionData(questionDataArray[newIndex]);
    }

    // Call external question change handler if provided
    if (onQuestionChange) {
      onQuestionChange(isPrev);
    }
    setShowSolution(false);
  };

  const handleCloseDrawer = () => {
    document.body.style.overflow = 'unset';
    handleClose();
  };

  const handleViewSolution = () => {
    setShowSolution(true);
  };

  // Adding this function to handle solution visblity from child
  const handleShowSolution = (shouldShow) => {
    setShowSolution(shouldShow);
  };

  if (!isRendered || !portalRoot) return null;

  // Determine which testId and packId to use for Solutions component
  let solutionTestId = testId;
  let solutionPackId = validPackId;

  if (isPackAnalysis && currentQuestionData) {
    // Use specific testId from current question data for pack analysis
    solutionTestId = currentQuestionData.testId;
  }

  const drawerContent = (
    <div
      className="fixed inset-0 flex justify-end"
      style={{
        zIndex: 2147483647,
        position: 'fixed',
        isolation: 'isolate',
      }}
    >
      {/* Backdrop with blur effect */}
      <div
        className={`absolute inset-0 transition-opacity duration-700 ease-in-out backdrop-blur-lg ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleCloseDrawer}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          zIndex: 2147483646,
        }}
      />

      {/* Drawer panel */}
      <div
        className={`relative flex flex-col h-full bg-[var(--q3-surface-default)] shadow-xl w-full lg:max-w-[946px] transition-transform duration-700 ease-in-out ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        onTransitionEnd={handleAnimationEnd}
        style={{ zIndex: 2147483647 }}
      >
        {/* Close button for desktop */}
        <Button
          onClick={handleCloseDrawer}
          size="icon"
          className="hidden lg:inline-flex rounded-full absolute top-5 -left-10 w-8 h-8 bg-[var(--q3-neutral-default)]"
          style={{ zIndex: 2147483647 }}
        >
          <XIcon size={24} className="text-[var(--q3-neutral-default_inverted)]" />
        </Button>

        {/* Mobile header with close button */}
        <div className="border-b border-[var(--q3-stroke-normal)] p-4 flex items-center justify-between lg:hidden">
          <Button
            onClick={handleCloseDrawer}
            size="icon"
            className="rounded-full w-8 h-8 bg-transparent hover:bg-[var(--q3-surface-dim)]"
          >
            <XIcon size={24} className="text-[var(--q3-neutral-default)]" />
          </Button>
        </div>

        {/* Solutions content */}
        <div className="flex-1 overflow-y-auto w-full">
          <Solutions
            key={isPackAnalysis ? `${questionNumber}-${solutionTestId}` : solutionTestId}
            qNo={questionNumber}
            isDrawer={true}
            showSolution={showSolution} // This should control solution visibility
            onShowSolution={handleShowSolution}
            {...(solutionPackId && { packId: solutionPackId })}
            {...(solutionTestId && { testId: solutionTestId })}
            showReportButton={showReportButton}
            questionData={currentQuestionData}
          />
        </div>

        {/* Footer navigation */}
        <div className="border-t border-[var(--q3-stroke-normal)] p-4 bg-[var(--q3-surface-default)]">
          <div className="flex items-center w-full gap-3">
            <Button
              onClick={() => handleQuestionChange(true)}
              variant="outline"
              disabled={isPackAnalysis ? currentQnoIndex === 0 : questionNumber === questionNumbers[0]}
              className="flex-1 rounded-full bg-transparent"
            >
              <CaretLeftIcon className="w-4 h-4 mr-1" /> Previous
            </Button>

            {/* View Solution Button - Only show when solution is not visible */}
            {!showSolution && (
              <Button
                onClick={handleViewSolution}
                className="flex-1 rounded-full bg-[var(--q3-accent-normal)] text-[var(--q3-neutral-default_inverted)] hover:bg-[var(--q3-accent-normal)]"
              >
                <EyeIcon className="w-4 h-4 mr-1" /> View Solution
              </Button>
            )}

            <Button
              onClick={() => handleQuestionChange(false)}
              variant="outline"
              disabled={
                isPackAnalysis
                  ? currentQnoIndex === questionNumbers.length - 1
                  : questionNumber === questionNumbers[questionNumbers.length - 1]
              }
              className="flex-1 rounded-full bg-transparent"
            >
              Next <CaretRightIcon className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(drawerContent, portalRoot);
}
